import { 
    useState,
    useEffect
} from "react";

import {
    Form,
    InputGroup,
    Button,
    Row,
    Badge
} from "react-bootstrap";

import {
    useDispatch,
    useSelector
} from "react-redux";

import {
    showMsg,
    hideMsg,
    requestLogout,
    showLoading,
    hideLoading,
    pageSelector
} from "./store/pageSlice";

import {
    FetchGeneralRegistry,
    GetRegistry,
    DeleteRegistry,
    AddRegistry,
    UpdateRegistry
} from "./api/generalRegistry";

import {
    setRegistry,
    resetRegistry,
    updateValRegistryName,
    updateValDescription,
    addKey,
    resetRefreshKeys,
    generalRegSelector
} from "./store/generalRegSlice";

import GeneralRegistryRow from "./GeneralRegistryRow";

import "./App.css";

export default function GeneralRegistryPage() {
    const dispatch = useDispatch();

    const genRegSel = useSelector(generalRegSelector);
    const pageSel = useSelector(pageSelector);

    const [search, setSearch] = useState("");

    const [regList, setRegList] = useState(null);
    const [dsReg, setDsReg] = useState([]);
    
    const [keyList, setKeyList] = useState(null);

    const [qryRow, setQryRow] = useState(null);

    const [isRefresh, setIsRefresh] = useState(true);

    const submitSearch = async (e) => {
        e.preventDefault();

        dispatch(showLoading());

        try {
            const result = await FetchGeneralRegistry();

            const srch = result.filter((row) => {
                const searchKey = search.toLowerCase();

                return row.registryName.toLowerCase().indexOf(searchKey) >= 0 ||
                    row.description.toLowerCase().indexOf(searchKey) >= 0;
            });

            setDsReg(srch);
        } catch (ex) {
            console.error(ex.message);

            if (ex.status !== 401) {
                dispatch(showMsg({
                    title: "Error Company Configuration",
                    message: `Unable to fetch registry listing. ${ex.message}`
                }));   
            } else {
                // request logout
                dispatch(requestLogout());
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    const refreshList = async () => {
        dispatch(showLoading());

        try {
            const result = await FetchGeneralRegistry();
            setDsReg(result);
        } catch (ex) {
            console.error(ex.message);

            if (ex.status !== 401) {
                dispatch(showMsg({
                    title: "Error Company Configuration",
                    message: `Unable to fetch registry listing. ${ex.message}`
                }));   
            } else {
                // request logout
                dispatch(requestLogout());
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    const onDelete = () => {
        dispatch(showMsg({
            title: "Confirm Delete",
            message: "Are you sure you want to delete selected registry?",
            msgId: "DEL_REG"
        }));
    };

    const onNewRegistry = () => {
        setQryRow(null);
        dispatch(resetRegistry());
    };

    const onCancelChanges = async () => {
        const id = qryRow;

        await loadQryRow();
    };

    const onSaveChanges = async (e)  => {
        e.preventDefault();

        dispatch(showLoading());

        try {
            if (/\w+/i.exec(genRegSel.registryName) === null) {
                throw {
                    status: 401,
                    message: "Registry name is required."
                };
            }

            let reg = {
                registryName: genRegSel.registryName,
                description: genRegSel.description,
                lastUpdate: genRegSel.lastUpdate,
                lastUpdateByUser: genRegSel.lastUpdateByUser,
                keys: genRegSel.keys
            };

            if (genRegSel.id !== "-1") {
                reg = {
                    ...reg,
                    id: genRegSel.id
                };

                await UpdateRegistry(reg);
            } else {
                await AddRegistry(reg);
            }

            dispatch(showMsg({
                title: "Configuration Saved",
                message: "Configuration has been successfully saved."
            }));

            setIsRefresh(true);
        } catch (ex) {
            console.error(ex.message);

            if (ex.status !== 401) {
                dispatch(showMsg({
                    title: "Error General Registry",
                    message: `An error occured during saving registry. ${ex.message}`
                }));
            } else {
                // request logout
                dispatch(requestLogout());
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    const loadQryRow = async () => {
        dispatch(showLoading());

        try {
            const result = await GetRegistry(qryRow);

            if (result === null) {
                dispatch(resetRegistry());
                return;
            }

            dispatch(setRegistry(result));
        } catch (ex) {
            console.error(ex.message);

            if (ex.status !== 401) {
                dispatch(showMsg({
                    title: "Error General Registry",
                    message: `Unable to view selected registry. ${ex.message}`
                }));
            } else {
                // request logout
                dispatch(requestLogout());
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    useEffect(() => {
        if (!pageSel.msgResult || pageSel.msgId === null) {
            return;
        }

        (
            async () => {
                if (pageSel.msgId == "DEL_REG") {
                    dispatch(resetRegistry());

                    await DeleteRegistry(qryRow);
                    
                    setQryRow(null);

                    await refreshList();
                }
        
                dispatch(hideMsg());
            }
        )();
    }, [pageSel.msgResult]);

    useEffect(() => {
        if (!isRefresh) {
            return;
        }

        (
            async () => {
                await refreshList();
                setIsRefresh(false);
            }
        )();
    }, [isRefresh]);

    useEffect(() => {
        const lst = dsReg.map((row) => {
            return (
                <div 
                    key={ row.id }
                    className="list-item"
                    onClick={() => { setQryRow(row.id) }}
                >
                    <strong>{ row.registryName }</strong>
                    <div className="list-item-section">
                        <span>{ row.description }</span>
                    </div>
                </div>
            );
        });

        setRegList(lst);
    }, [dsReg]);

    useEffect(() => {
        if (qryRow === null) {
            return;
        }

        (
            async () => {
                await loadQryRow();
            }
        )();
    }, [qryRow]);

    useEffect(() => {
        if (!genRegSel.isRefreshKeys) {
            return;
        }

        const keys = genRegSel.keys.map((row) => row);

        const lst = [];

        for (let i = 0; i < keys.length; i++) {
            const row = genRegSel.keys[i];

            lst.push(
                <GeneralRegistryRow 
                    key={ row.key + i } 
                    keyIndex={ i }
                    initialData={{
                        key: row.key,
                        value: row.value
                    }} 
                />
            );
        }

        setKeyList(lst);

        dispatch(resetRefreshKeys());
    }, [genRegSel.isRefreshKeys]);

    return (
        <>
            <div className="editor-panel">
                <div className="search-panel">
                    <Form onSubmit={ submitSearch }>
                        <InputGroup className="mb-3">
                            <Button variant="success" onClick={ refreshList }><img src="img/replay_circle_filled_white_36dp.svg" /></Button>
                            <Form.Control placeholder="Enter registry name" onChange={(e) => { setSearch(e.target.value) }} />
                            <Button type="submit"><img src="img/search_white_36dp.svg" /></Button>
                        </InputGroup>
                    </Form>
                </div>
                <div className="editor-section">
                    <strong>Registry Details</strong>
                    <Button onClick={ onNewRegistry }><img src="img/add_circle_white_36dp.svg" />Create New Registry</Button>
                </div>
                <Form onSubmit={ onSaveChanges }>
                    <div className="editor-body">
                        <Form.Group className="mb-3">
                            <Form.Label>Registry Name</Form.Label>
                            <Form.Control required={ true } readOnly={ genRegSel.id !== "-1" } onChange={(e) => { dispatch(updateValRegistryName(e.target.value)) }} value={ genRegSel.registryName } />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control required={ true } onChange={(e) => { dispatch(updateValDescription(e.target.value)) }} value={ genRegSel.description } />
                        </Form.Group>
                        <Row className="mb-3">
                            <div className="editor-button-box">
                                <Button variant="danger" onClick={ onDelete }><img src="img/delete_outline_white_24dp.svg" />Delete Registry</Button>
                            </div>
                        </Row>
                    </div>
                    <div className="editor-section">
                        <strong>Keys</strong>
                        <Button variant="success" onClick={() => { dispatch(addKey()) }}><img src="img/add_circle_white_36dp.svg" /></Button>
                    </div>
                    <div className="editor-sub-body">
                        <table>
                            <thead>
                                <tr>
                                    <th scope="col">Key</th>
                                    <th scope="col">Value</th>
                                    <th scope="col"></th>
                                </tr>
                            </thead>
                            <tbody>
                                { keyList }
                            </tbody>
                        </table>
                        {
                            genRegSel.isChangesMade && (
                                <div className="editor-button-box">
                                    <Button variant="success" type="submit"><img className="button-icon" src="img/save_white_36dp.svg" />Save</Button>
                                    <Button variant="danger" onClick={ onCancelChanges }><img className="button-icon" src="img/cancel_white_36dp.svg" />Cancel</Button>
                                </div>
                            )
                        }
                    </div>
                </Form>
            </div>
            <div className="listing">
                { regList }
            </div>
        </>
    );
}